import React, { useState, useMemo } from "react";
import { Trash2, Image, Filter } from "lucide-react";
import { Sticker } from "../../types";
import OptimizedImage from "../OptimizedImage";

interface StickersGridProps {
  stickers: Sticker[];
  onDeleteSticker: (id: string) => void;
}

const StickersGrid: React.FC<StickersGridProps> = ({
  stickers,
  onDeleteSticker,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Get unique categories from stickers
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(stickers.map((sticker) => sticker.category))
    );
    return uniqueCategories.sort();
  }, [stickers]);

  // Filter stickers based on selected category
  const filteredStickers = useMemo(() => {
    if (selectedCategory === "all") {
      return stickers;
    }
    return stickers.filter((sticker) => sticker.category === selectedCategory);
  }, [stickers, selectedCategory]);
  if (stickers.length === 0) {
    return (
      <div className="text-center py-12">
        <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2" dir="rtl">
          עדיין אין סטיקרים
        </h3>
        <p className="text-gray-600" dir="rtl">
          הסטיקרים הראשונים יופיעו כאן
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Category Filter */}
      <div className="flex items-center space-x-4" dir="rtl">
        <Filter className="h-5 w-5 text-gray-600" />
        <label className="text-sm font-medium text-gray-700">
          סינון לפי קטגוריה:
        </label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
          dir="rtl"
        >
          <option value="all">הכל ({stickers.length})</option>
          {categories.map((category) => {
            const count = stickers.filter(
              (s) => s.category === category
            ).length;
            return (
              <option key={category} value={category}>
                {category} ({count})
              </option>
            );
          })}
        </select>
      </div>

      {/* Stickers Grid */}
      {filteredStickers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600" dir="rtl">
            לא נמצאו סטיקרים בקטגוריה זו
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredStickers.map((sticker) => (
            <div
              key={sticker.id}
              className="bg-white/50 rounded-lg overflow-hidden group border border-pink-200 hover:shadow-lg transition-shadow"
            >
              <div className="relative">
                <OptimizedImage
                  src={sticker.image_url}
                  thumbnailSrc={sticker.thumbnail_url}
                  alt={sticker.name}
                  className="w-full h-32 object-cover object-center"
                  style={{ width: "100%", height: "128px" }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://via.placeholder.com/300x200?text=Sticker";
                  }}
                />
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                  <button
                    onClick={() => onDeleteSticker(sticker.id)}
                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    title="מחק"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p
                  className="text-sm font-semibold text-gray-800 text-center mb-1"
                  dir="rtl"
                >
                  {sticker.name}
                </p>
                <p className="text-xs text-gray-600 text-center">
                  {sticker.category} •{" "}
                  {new Date(sticker.created_at).toLocaleDateString("he-IL")}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StickersGrid;
